// KOA-5857 — Pure helpers for the expanded String editor.
//
// Extracted from `cell-expander-modal.tsx` so unit tests can import
// them without loading the React component graph (which pulls in
// `KModal` → `rc-dialog` and trips up vitest's module resolver).
// Anything the modal does inside a `useMemo` over pure inputs lives
// here.

import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";

export interface IJsonProbeOk {
  ok: true;
  value: unknown;
}
export interface IJsonProbeFail {
  ok: false;
}
export type IJsonProbe = IJsonProbeOk | IJsonProbeFail;

export const tryParseJson = (s: string): IJsonProbe => {
  if (s.length === 0) {
    return { ok: false };
  }
  const trimmed: string = s.trim();
  if (trimmed.length === 0) {
    return { ok: false };
  }
  // Heuristic: only attempt JSON-shaped input — bare numbers and
  // quoted strings would parse as JSON literals and the Pretty toggle
  // would offer meaningless reformatting.
  const first: string = trimmed[0]!;
  if (first !== "{" && first !== "[") {
    return { ok: false };
  }
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    return { ok: false };
  }
};

export interface ITextStats {
  chars: number;
  lines: number;
  isWhitespaceOnly: boolean;
}

export const statsFor = (text: string): ITextStats => {
  if (text.length === 0) {
    return { chars: 0, lines: 0, isWhitespaceOnly: false };
  }
  const lineCount: number = text.split("\n").length;
  return {
    chars: text.length,
    lines: lineCount,
    isWhitespaceOnly: text.trim().length === 0
  };
};

export type CellValueKind = "null" | "empty" | "whitespace" | "value";

export const classifyValue = (isNull: boolean, text: string): CellValueKind => {
  if (isNull) {
    return "null";
  }
  if (text.length === 0) {
    return "empty";
  }
  if (text.trim().length === 0) {
    return "whitespace";
  }
  return "value";
};

export interface IValidation {
  // Blocking — Apply disabled.
  error: string | null;
  // Non-blocking — Apply works.
  warning: string | null;
}

export const validateAgainstColumn = (
  column: IDatabaseColumnModel,
  isNull: boolean,
  text: string,
  stats: ITextStats
): IValidation => {
  if (isNull && column.nullable === false) {
    return { error: "This column doesn't accept null.", warning: null };
  }
  if (isNull) {
    return { error: null, warning: null };
  }
  const max: number | null | undefined = column.maxLength;
  if (typeof max === "number" && max > 0) {
    if (stats.chars > max) {
      return {
        error: `Too long: max ${max.toLocaleString("en-US")} characters (you have ${stats.chars.toLocaleString("en-US")}).`,
        warning: null
      };
    }
    if (stats.chars >= Math.floor(max * 0.95)) {
      return {
        error: null,
        warning: `Near the column's ${max.toLocaleString("en-US")}-character limit (${stats.chars.toLocaleString("en-US")} so far).`
      };
    }
  }
  if (stats.isWhitespaceOnly && stats.chars > 0) {
    return { error: null, warning: "Value is whitespace only." };
  }
  return { error: null, warning: null };
};

// True when the text contains whitespace that benefits from
// visualisation — anything a reader can't see by glancing at the
// surface but that the database WILL store. Single spaces between
// words are expected prose, not "invisibles worth flagging", so
// they don't count. The triggers:
//   * Tab characters — invisible width, easy to miss.
//   * Leading whitespace — typically unintended.
//   * Trailing whitespace — typically unintended; survives copy/
//     paste and can break joins / WHERE clauses on string keys.
//   * Runs of 2+ consecutive spaces — usually a typo or paste
//     artifact.
// Newlines alone don't trigger — they're visible as line breaks
// and don't surprise the user. The Show-invisibles toolbar button
// uses this to surface the affordance only when the content
// actually has something worth visualising.
export const hasSuspiciousWhitespace = (text: string): boolean => {
  if (text.length === 0) {
    return false;
  }
  if (text.includes("\t")) {
    return true;
  }
  if (text !== text.trim()) {
    return true;
  }
  return /  +/.test(text);
};

const escapeHtml = (s: string): string => {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
};

// Wraps spaces / tabs / line breaks in styled spans for the
// show-invisibles toggle. Output composed with pre-escaped raw input
// so an injected `<script>` in the source never reaches the DOM as
// code. Mutually exclusive with JSON highlighting — the modal toggles
// one or the other.
export const renderInvisibles = (text: string): string => {
  let out: string = escapeHtml(text);
  out = out.replace(/ /g, '<span class="kuika-cell-expander-ws kuika-cell-expander-ws--space">·</span>');
  out = out.replace(/\t/g, '<span class="kuika-cell-expander-ws kuika-cell-expander-ws--tab">→\t</span>');
  out = out.replace(/\n/g, '<span class="kuika-cell-expander-ws kuika-cell-expander-ws--nl">¶</span>\n');
  return out;
};
